'use client';

import { forwardRef } from 'react';
import type { ReactNode } from 'react';
import { Text as MantineText, createPolymorphicComponent } from '@mantine/core';
import type { TextProps as MantineTextProps } from '@mantine/core';

import type { LockedPolymorphic, LockedProps } from '../types';

export type TextProps = Omit<MantineTextProps, LockedProps> & { children?: ReactNode };

/**
 * ### Text
 *
 * @description
 * Mantine's Text minus per-call-site style overrides (see {@link LockedProps}).
 * Style with Mantine props: `size`, `c`, `fw`, `tt`, `ta`, etc. `className`/`style` are locked even
 * though the component is polymorphic: the intrinsic element props would otherwise re-widen them
 * back in.
 *
 * @example
 * ```tsx
 * <Text>Body text</Text>
 * <Text size="sm" c="dimmed">Caption text</Text>
 * <Text size="sm" tt="capitalize" fw={600}>Field label</Text>
 * ```
 *
 * @param props - {@link TextProps}
 * @returns React component
 */
const _Text = forwardRef<HTMLParagraphElement, TextProps>((props, ref) => (
  <MantineText ref={ref} {...props} />
));
_Text.displayName = 'Text';

export const Text = createPolymorphicComponent<'p', TextProps>(_Text) as LockedPolymorphic<
  'p',
  TextProps
>;
